import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { partnerProfileExtrasApi } from '../../api/partner-profile-extras';
import { ApiError } from '../../api/client';
import { IconActionButton } from '../../components/IconActionButton';
import { Trash2 } from 'lucide-react';
import { useToast } from '../../components/Toast';

/** Organization Profile (spec section 8) - sectors, geographic coverage, donors, coordination memberships. Real many-to-many/one-to-many data, not delimited strings. */
export function PartnerOrganizationProfileTab({ partnerId }: { partnerId: number }) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [selectedSectorId, setSelectedSectorId] = useState('');
  const [newState, setNewState] = useState('');
  const [newLga, setNewLga] = useState('');
  const [newDonor, setNewDonor] = useState('');
  const [newMembership, setNewMembership] = useState('');

  const sectorsQuery = useQuery({ queryKey: ['partner-sectors', partnerId], queryFn: () => partnerProfileExtrasApi.listSectors(partnerId) });
  const allSectorsQuery = useQuery({ queryKey: ['all-sectors'], queryFn: () => partnerProfileExtrasApi.allSectors() });
  const geoQuery = useQuery({ queryKey: ['partner-geography', partnerId], queryFn: () => partnerProfileExtrasApi.listGeography(partnerId) });
  const donorsQuery = useQuery({ queryKey: ['partner-donors', partnerId], queryFn: () => partnerProfileExtrasApi.listDonors(partnerId) });
  const membershipsQuery = useQuery({ queryKey: ['partner-memberships', partnerId], queryFn: () => partnerProfileExtrasApi.listCoordinationMemberships(partnerId) });

  const onErr = (err: unknown) => showError(err instanceof ApiError ? err.message : 'Failed.');

  const linkSectorMutation = useMutation({ mutationFn: () => partnerProfileExtrasApi.linkSector(partnerId, Number(selectedSectorId)), onSuccess: () => { showSuccess('Sector added.'); queryClient.invalidateQueries({ queryKey: ['partner-sectors', partnerId] }); setSelectedSectorId(''); }, onError: onErr });
  const unlinkSectorMutation = useMutation({ mutationFn: (sectorId: number) => partnerProfileExtrasApi.unlinkSector(partnerId, sectorId), onSuccess: () => { showSuccess('Removed.'); queryClient.invalidateQueries({ queryKey: ['partner-sectors', partnerId] }); }, onError: onErr });

  const addGeoMutation = useMutation({ mutationFn: () => partnerProfileExtrasApi.addGeography(partnerId, { state: newState, lga: newLga || undefined }), onSuccess: () => { showSuccess('Added.'); queryClient.invalidateQueries({ queryKey: ['partner-geography', partnerId] }); setNewState(''); setNewLga(''); }, onError: onErr });
  const removeGeoMutation = useMutation({ mutationFn: (id: number) => partnerProfileExtrasApi.removeGeography(id), onSuccess: () => { showSuccess('Removed.'); queryClient.invalidateQueries({ queryKey: ['partner-geography', partnerId] }); }, onError: onErr });

  const addDonorMutation = useMutation({ mutationFn: () => partnerProfileExtrasApi.addDonor(partnerId, newDonor), onSuccess: () => { showSuccess('Added.'); queryClient.invalidateQueries({ queryKey: ['partner-donors', partnerId] }); setNewDonor(''); }, onError: onErr });
  const removeDonorMutation = useMutation({ mutationFn: (id: number) => partnerProfileExtrasApi.removeDonor(id), onSuccess: () => { showSuccess('Removed.'); queryClient.invalidateQueries({ queryKey: ['partner-donors', partnerId] }); }, onError: onErr });

  const addMembershipMutation = useMutation({ mutationFn: () => partnerProfileExtrasApi.addCoordinationMembership(partnerId, newMembership), onSuccess: () => { showSuccess('Added.'); queryClient.invalidateQueries({ queryKey: ['partner-memberships', partnerId] }); setNewMembership(''); }, onError: onErr });
  const removeMembershipMutation = useMutation({ mutationFn: (id: number) => partnerProfileExtrasApi.removeCoordinationMembership(id), onSuccess: () => { showSuccess('Removed.'); queryClient.invalidateQueries({ queryKey: ['partner-memberships', partnerId] }); }, onError: onErr });

  const linkedSectorIds = new Set((sectorsQuery.data?.data ?? []).map((s) => s.sector_id));

  return (
    <div>
      <div className="panel">
        <div className="panel-body">
          <h3>Sectors</h3>
          <ul className="deadline-list">
            {sectorsQuery.data?.data.map((s) => <li key={s.id}>{s.name} <IconActionButton icon={Trash2} label="Remove" danger onClick={() => unlinkSectorMutation.mutate(s.sector_id)} /></li>)}
          </ul>
          <div className="inline-form">
            <select value={selectedSectorId} onChange={(e) => setSelectedSectorId(e.target.value)}>
              <option value="">- choose sector -</option>
              {allSectorsQuery.data?.data.filter((s) => !linkedSectorIds.has(s.id)).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <button disabled={!selectedSectorId || linkSectorMutation.isPending} onClick={() => linkSectorMutation.mutate()}>Add Sector</button>
          </div>
        </div>
      </div>

      <div className="panel">
        <div className="panel-body">
          <h3>Geographic Coverage</h3>
          <ul className="deadline-list">
            {geoQuery.data?.data.map((g) => <li key={g.id}>{g.state}{g.lga ? ` - ${g.lga}` : ''} <IconActionButton icon={Trash2} label="Remove" danger onClick={() => removeGeoMutation.mutate(g.id)} /></li>)}
          </ul>
          <div className="inline-form">
            <label>State<input value={newState} onChange={(e) => setNewState(e.target.value)} /></label>
            <label>LGA<input value={newLga} onChange={(e) => setNewLga(e.target.value)} /></label>
            <button disabled={!newState || addGeoMutation.isPending} onClick={() => addGeoMutation.mutate()}>Add</button>
          </div>
        </div>
      </div>

      <div className="panel">
        <div className="panel-body">
          <h3>Donors</h3>
          <ul className="deadline-list">
            {donorsQuery.data?.data.map((d) => <li key={d.id}>{d.donor_name} <IconActionButton icon={Trash2} label="Remove" danger onClick={() => removeDonorMutation.mutate(d.id)} /></li>)}
          </ul>
          <div className="inline-form">
            <label>Donor<input value={newDonor} onChange={(e) => setNewDonor(e.target.value)} /></label>
            <button disabled={!newDonor || addDonorMutation.isPending} onClick={() => addDonorMutation.mutate()}>Add</button>
          </div>
        </div>
      </div>

      <div className="panel">
        <div className="panel-body">
          <h3>Coordination Memberships</h3>
          <ul className="deadline-list">
            {membershipsQuery.data?.data.map((m) => <li key={m.id}>{m.membership_name} <IconActionButton icon={Trash2} label="Remove" danger onClick={() => removeMembershipMutation.mutate(m.id)} /></li>)}
          </ul>
          <div className="inline-form">
            <label>Membership<input value={newMembership} onChange={(e) => setNewMembership(e.target.value)} /></label>
            <button disabled={!newMembership || addMembershipMutation.isPending} onClick={() => addMembershipMutation.mutate()}>Add</button>
          </div>
        </div>
      </div>
    </div>
  );
}
